import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import { useRecoilValue } from "recoil";
import { useSnackbar } from "notistack";
import { authState } from "../../Recoil/RecoilState";
import { fetchLostItems, setLostItemStatus } from "../../services/lostFoundApi";
import { LOST_STATUSES, LOST_STATUS_COLOR, errorText, formatDateTime, titleCase } from "./lostFoundConstants";
import ReportLostItemDialog from "./ReportLostItemDialog";
import LostItemMatches from "./LostItemMatches";

/** Lost reports: file one, list them, and open one to review its match candidates. */
const LostReportsTab = () => {
  const { token } = useRecoilValue(authState);
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reporting, setReporting] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchLostItems({ status: status || undefined, page: page + 1, per_page: perPage }, token));
    } catch (err) {
      setError(errorText(err, "Could not load lost reports."));
    } finally {
      setLoading(false);
    }
  }, [status, page, perPage, token]);

  useEffect(() => {
    if (token) load();
  }, [load, token]);

  const close = async (item) => {
    try {
      await setLostItemStatus(item.lost_id, "closed", token);
      enqueueSnackbar("Report closed.", { variant: "info" });
      load();
    } catch (err) {
      enqueueSnackbar(errorText(err), { variant: "error" });
    }
  };

  if (selected) {
    return <LostItemMatches lostId={selected} onBack={() => setSelected(null)} onChanged={load} />;
  }

  const items = data?.items || [];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} spacing={2}>
        <TextField select size="small" label="Status" value={status} sx={{ minWidth: 180 }}
          onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
          <MenuItem value="">All statuses</MenuItem>
          {LOST_STATUSES.map((value) => (
            <MenuItem key={value} value={value}>{titleCase(value)}</MenuItem>
          ))}
        </TextField>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setReporting(true)}>
          Report lost item
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        {loading && !data ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : items.length ? (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 64 }}>Photo</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Object</TableCell>
                    <TableCell>Colour</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Reported</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.lost_id} hover sx={{ cursor: "pointer" }} onClick={() => setSelected(item.lost_id)}
                      data-testid="lost-report-row">
                      <TableCell>
                        <Avatar variant="rounded" src={item.photo_path || undefined} alt="">
                          <AssignmentLateOutlinedIcon />
                        </Avatar>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="body2" noWrap>{item.description || "Photo-only report"}</Typography>
                      </TableCell>
                      <TableCell>{item.class_name ? titleCase(item.class_name) : "—"}</TableCell>
                      <TableCell>{item.dominant_color ? titleCase(item.dominant_color) : "—"}</TableCell>
                      <TableCell>{item.reporter_name || "—"}</TableCell>
                      <TableCell>{formatDateTime(item.reported_at)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={titleCase(item.status)} color={LOST_STATUS_COLOR[item.status] || "default"} />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Button size="small" onClick={() => setSelected(item.lost_id)}>
                          Matches{item.suggestions ? ` (${item.suggestions})` : ""}
                        </Button>
                        {item.status === "open" && (
                          <Button size="small" color="inherit" onClick={() => close(item)}>Close</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={data?.total ?? 0}
              page={page}
              rowsPerPage={perPage}
              rowsPerPageOptions={[10, 20, 50]}
              onPageChange={(_, next) => setPage(next)}
              onRowsPerPageChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }}
            />
          </>
        ) : (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <AssignmentLateOutlinedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
            <Typography variant="h6" color="text.secondary">No lost reports</Typography>
            <Typography variant="body2" color="text.secondary">
              File a report with a photo or description to search the found gallery for it.
            </Typography>
          </Box>
        )}
      </Card>

      <ReportLostItemDialog
        open={reporting}
        onClose={() => setReporting(false)}
        onFiled={(report) => {
          setReporting(false);
          enqueueSnackbar("Report filed. Ranking the found gallery against it now.", { variant: "success" });
          load();
          if (report?.lost_id) setSelected(report.lost_id);
        }}
      />
    </Box>
  );
};

export default LostReportsTab;
